import classNames from 'classnames'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import React from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { commonIcons } from '@apps-common/config/icons'
import { ISelectOption } from '@apps-common/types/form'

const variants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  registration: Partial<UseFormRegister<any>>
  containerClassName?: string
  labelClassName?: string
  label: string
  showLabel?: boolean
  errors?: FieldError
  size?: 'sm' | 'lg'
  fixedLoading?: boolean
  loading?: boolean
  disabled?: boolean
  options: ISelectOption[]
}

export const SelectInputInfoSM = React.forwardRef<HTMLInputElement, React.PropsWithChildren<InputProps>>((props: InputProps, ref) => {
  const {
    className,
    containerClassName,
    labelClassName,
    registration,
    placeholder,
    autoComplete = 'off',
    id,
    showLabel = false,
    label,
    errors,
    fixedLoading = false,
    loading = false,
    options
  } = props

  const Icon = commonIcons.caretDown

  const defaultOptions = []

  if (placeholder && placeholder?.length > 0) {
    defaultOptions.push(
      <option key={`${id}-select-option-placeholder`} selected value=''>
        {placeholder}
      </option>
    )
  }

  return (
    <AnimatePresence mode='wait'>
      {loading && (
        <motion.div
          key={`${id}-select-sm-skeleton`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2', !fixedLoading && 'animate-pulse', containerClassName)}
        >
          <div className={classNames('block ml-2 mb-2 h-[16px] bg-skeleton max-w-[20%] rounded-lg', { 'sr-only': !showLabel })} />
          <div className='relative'>
            <div className={classNames('px-4 py-2 rounded-3xl bg-skeleton w-full h-[36px]', className)} />
          </div>
          <p className='pl-2 pt-2 h-[5px]' />
        </motion.div>
      )}
      {!loading && (
        <motion.div
          key={`${id}-select-sm`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2', containerClassName)}
        >
          <label
            className={classNames('block pl-2 pb-2 text-sm text-secondary-content font-bold', labelClassName, { 'sr-only': !showLabel })}
            htmlFor={id}
          >
            {label}
          </label>
          <div className='relative flex items-center'>
            <select
              id={id}
              // placeholder={placeholder}
              className={classNames(
                'input !border-none text-secondary-content w-full text-sm  h-[35px] sm:h-[40px] rounded-[20px] !outline-none disable-input !px-[10px] bg-secondary',
                { 'input-error': errors },
                '!text-secondary-content !bg-secondary',
                className
              )}
              disabled
              // autoComplete={autoComplete}
              {...registration}
            >
              {[
                ...defaultOptions,
                ...options.map((o, i) => (
                  <option key={`${id}-select-option-${i}`} value={o.value?.toString()}>
                    {o.label}
                  </option>
                ))
              ]}
            </select>
            {/* <Icon className='absolute right-[10px]' /> */}
          </div>
          <p className='pl-2 pt-2 text-rose-600 text-xs'>{errors?.message}</p>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

SelectInputInfoSM.displayName = 'SelectInputInfoSM'
